/**
 * Settings → Import (contracts imports.ts): the review step's rules, pure. Slack: who each Slack person becomes and
 * which conversations come over (channels by default, DMs only when picked — Slack's importer asks the same way).
 * Notion: editing the proposed structure (sections, which top-level pages go where, database views) before starting.
 */
import type {
  ImportJob,
  ImportStatus,
  NotionImportPlan,
  NotionImportSection,
  PageVisibility,
  SlackImportChannel,
  SlackImportPreview,
  SlackImportUser,
  SlackPersonChoice,
  StartImportRequest,
  ViewLayout,
} from "@bundle/contracts";

/** The job is still moving (the client polls it). */
export function isRunning(status: ImportStatus): boolean {
  return status === "scanning" || status === "importing";
}

/** 0…1 for a progress bar (undefined while the total isn't known). */
export function progressFraction(job: ImportJob): number | undefined {
  const p = job.progress;
  if (p === undefined || p.total === undefined || p.total === 0) return undefined;
  return Math.min(1, p.done / p.total);
}

// ---------- Slack ----------

export type SlackChoice = { include: boolean; name: string };

export type SlackReview = {
  /** Slack user id → what happens to them (Slack's importer choices; contracts `SlackPersonAction`). */
  people: Record<string, SlackPersonChoice>;
  channels: Record<string, SlackChoice>;
  /** Copy attached files into Bundle. */
  files: boolean;
  /** Email the invites now (else they wait in Invitations, to resend). */
  sendInvites: boolean;
  /** People from another company only get the channels they were in (restricted guests). */
  restrictExternal: boolean;
};

const isDm = (c: SlackImportChannel) => c.kind === "dm" || c.kind === "group_dm";

/** The server's suggestion for a person (member when matched by email). */
function suggestion(u: SlackImportUser): SlackPersonChoice {
  return u.suggested === "member" && u.matchUserId !== undefined ? { action: "member", userId: u.matchUserId } : { action: u.suggested === "member" ? "name" : u.suggested };
}

/**
 * What can be done with a person: invites need an email; bots only keep their name. With `restrictExternal`, people
 * from another company never get a full-member invite: guest (only their channels) or a deactivated profile.
 */
export function personActions(u: SlackImportUser, restrictExternal = true): SlackPersonChoice["action"][] {
  if (u.bot) return ["name"];
  const out: SlackPersonChoice["action"][] = ["member"];
  if (u.email && !u.deleted) out.push(...(u.external && restrictExternal ? (["guest"] as const) : (["invite", "guest"] as const)));
  out.push("deactivated");
  out.push("name");
  return [...new Set(out)];
}

/**
 * The defaults: the server's suggestions per person, every channel (archived ones too), no DMs, files copied. A
 * person's own import (`personal`) is about their DMs: everything is in.
 */
export function slackDefaults(p: SlackImportPreview, opts: { personal?: boolean } = {}): SlackReview {
  const people: Record<string, SlackPersonChoice> = {};
  for (const u of p.users) people[u.slackId] = suggestion(u);
  const channels: Record<string, SlackChoice> = {};
  for (const c of p.channels) channels[c.slackId] = { include: opts.personal === true || !isDm(c), name: c.name };
  return { people, channels, files: true, sendInvites: true, restrictExternal: true };
}

/**
 * The external-people rule for the whole import: on, external people's member invites become guest invites; off,
 * their guest invites become member invites. Deactivated profiles follow the rule on the server.
 */
export function setRestrictExternal(p: SlackImportPreview, r: SlackReview, on: boolean): SlackReview {
  const people = { ...r.people };
  for (const u of p.users) {
    if (!u.external) continue;
    const cur = people[u.slackId];
    if (on && cur?.action === "invite") people[u.slackId] = { action: "guest" };
    if (!on && cur?.action === "guest") people[u.slackId] = { action: "invite" };
  }
  return { ...r, people, restrictExternal: on };
}

/** "Invite everyone missing": people not matched to a member are invited (external ones as guests); who left stays deactivated. */
export function inviteAllMissing(p: SlackImportPreview, r: SlackReview): SlackReview {
  const people = { ...r.people };
  for (const u of p.users) {
    const cur = people[u.slackId];
    if (u.bot || !u.email || u.deleted || cur?.action === "member") continue;
    people[u.slackId] = { action: u.external && r.restrictExternal ? "guest" : "invite" };
  }
  return { ...r, people };
}

/** A channel name as the server will keep it (lowercase, dashes, ≤ 80). */
export function cleanChannelName(raw: string): string {
  return raw.toLowerCase().replace(/[^\p{L}\p{N}_-]+/gu, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

/** Problems that block starting: a channel picked twice under one name, or an empty name. */
export function slackProblems(p: SlackImportPreview, r: SlackReview): { duplicateNames: string[]; emptyNames: string[] } {
  const seen = new Map<string, number>();
  const empty: string[] = [];
  for (const c of p.channels) {
    const choice = r.channels[c.slackId];
    if (!choice?.include || isDm(c)) continue;
    const name = cleanChannelName(choice.name);
    if (!name) empty.push(c.slackId);
    else seen.set(name, (seen.get(name) ?? 0) + 1);
  }
  return { duplicateNames: [...seen].filter(([, n]) => n > 1).map(([n]) => n), emptyNames: empty };
}

export function slackCounts(
  p: SlackImportPreview,
  r: SlackReview,
): { channels: number; dms: number; messages: number; matched: number; people: number; invite: number; guest: number; deactivated: number } {
  let channels = 0;
  let dms = 0;
  let messages = 0;
  for (const c of p.channels) {
    if (!r.channels[c.slackId]?.include) continue;
    if (isDm(c)) dms++;
    else channels++;
    messages += c.messageCount ?? 0;
  }
  const people = p.users.filter((u) => !u.bot);
  const count = (a: SlackPersonChoice["action"]) => people.filter((u) => r.people[u.slackId]?.action === a).length;
  return {
    channels, dms, messages, people: people.length,
    matched: count("member"), invite: count("invite"), guest: count("guest"), deactivated: count("deactivated"),
  };
}

export function slackStartRequest(p: SlackImportPreview, r: SlackReview): StartImportRequest {
  return {
    slack: {
      people: { ...r.people },
      channels: p.channels.map((c) => {
        const choice = r.channels[c.slackId] ?? { include: false, name: c.name };
        const name = cleanChannelName(choice.name);
        return !isDm(c) && name && name !== c.name ? { slackId: c.slackId, include: choice.include, name } : { slackId: c.slackId, include: choice.include };
      }),
      files: r.files,
      sendInvites: r.sendInvites,
      restrictExternal: r.restrictExternal,
    },
  };
}

// ---------- Notion ----------

/** Moves a top-level node into `sectionId` (or out of every section: `null` keeps it at the workspace top level). */
export function moveNode(plan: NotionImportPlan, key: string, sectionId: string | null): NotionImportPlan {
  const sections = plan.sections.map((s) => ({ ...s, nodeKeys: s.nodeKeys.filter((k) => k !== key) }));
  const target = sections.find((s) => s.id === sectionId);
  if (target) target.nodeKeys.push(key);
  return { ...plan, sections };
}

/** The section a top-level node is in (undefined: the workspace top level). */
export function sectionOf(plan: NotionImportPlan, key: string): NotionImportSection | undefined {
  return plan.sections.find((s) => s.nodeKeys.includes(key));
}

export function renameSection(plan: NotionImportPlan, id: string, title: string): NotionImportPlan {
  return { ...plan, sections: plan.sections.map((s) => (s.id === id ? { ...s, title: title.slice(0, 200) } : s)) };
}

export function setSectionVisibility(plan: NotionImportPlan, id: string, visibility: PageVisibility): NotionImportPlan {
  return { ...plan, sections: plan.sections.map((s) => (s.id === id ? { ...s, visibility } : s)) };
}

/** A new, empty section at the end (`id` from the caller's IdGen). */
export function addSection(plan: NotionImportPlan, id: string, title: string): NotionImportPlan {
  return { ...plan, sections: [...plan.sections, { id, title, visibility: "workspace", nodeKeys: [] }] };
}

/** Removes a section; its pages go back to the workspace top level. */
export function removeSection(plan: NotionImportPlan, id: string): NotionImportPlan {
  return { ...plan, sections: plan.sections.filter((s) => s.id !== id) };
}

export function setDatabaseLayout(plan: NotionImportPlan, key: string, layout: ViewLayout): NotionImportPlan {
  const rest = plan.databases.filter((d) => d.key !== key);
  const prev = plan.databases.find((d) => d.key === key);
  const keepColumn = prev?.column !== undefined && prev.layout === layout;
  return { ...plan, databases: [...rest, keepColumn ? { key, layout, column: prev.column } : { key, layout }] };
}

/** Whether a top-level page / database comes over (all do until left out). */
export function isIncluded(plan: NotionImportPlan, key: string): boolean {
  return !(plan.excluded ?? []).includes(key);
}

/** Includes or leaves out top-level pages / databases (with everything under them). */
export function setIncluded(plan: NotionImportPlan, keys: string[], include: boolean): NotionImportPlan {
  const out = new Set(plan.excluded ?? []);
  for (const k of keys) {
    if (include) out.delete(k);
    else out.add(k);
  }
  return { ...plan, excluded: [...out] };
}

/**
 * Sections must have a title (empty ones are dropped when starting), and something must be picked. Sections whose
 * pages are all left out don't count.
 */
export function notionProblems(plan: NotionImportPlan, topKeys?: string[]): { untitled: string[]; nothingPicked: boolean } {
  const picked = (s: NotionImportSection) => s.nodeKeys.some((k) => isIncluded(plan, k));
  return {
    untitled: plan.sections.filter((s) => picked(s) && s.title.trim() === "").map((s) => s.id),
    nothingPicked: topKeys !== undefined && !topKeys.some((k) => isIncluded(plan, k)),
  };
}

export function notionStartRequest(plan: NotionImportPlan): StartImportRequest {
  const sections = plan.sections
    .map((s) => ({ ...s, title: s.title.trim(), nodeKeys: s.nodeKeys.filter((k) => isIncluded(plan, k)) }))
    .filter((s) => s.nodeKeys.length > 0);
  return { notion: { plan: { ...plan, sections } } };
}
